import React from 'react';
import {
  IconBuilding,
  IconUserGroup,
  IconLock,
  IconPublic,
} from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import { Spinner, Thumbnail, token, Tooltip } from '@bynder/design-system';
import { EntityVisibility, PermissionType } from '../../types';
import { useIsEntityChanging } from '../../stores/socketsStore';

interface Props {
  isShared: boolean;
  entityVisibility: EntityVisibility;
  backgroundColor?: string;
  entityId: string;
  hasTooltip?: boolean;
  entityType: PermissionType;
}

interface IconProps {
  backgroundColor?: string;
}

const AllPortalUsersIcon: React.FC<IconProps> = ({ backgroundColor }) => (
  <Thumbnail
    backgroundColor={backgroundColor || token.gray25a}
    icon={<IconBuilding data-testid="portalIcon" />}
  />
);

const LockIcon: React.FC<IconProps> = ({ backgroundColor }) => (
  <Thumbnail
    icon={<IconLock data-testid="lockIcon" />}
    backgroundColor={backgroundColor || token.gray25a}
  />
);

const SpecificUsersIcon: React.FC<IconProps> = ({ backgroundColor }) => (
  <Thumbnail
    icon={<IconUserGroup data-testid="groupIcon" />}
    backgroundColor={backgroundColor || token.gray25a}
  />
);

const PublicIcon: React.FC<IconProps> = ({ backgroundColor }) => (
  <Thumbnail
    icon={<IconPublic data-testid="publicIcon" />}
    backgroundColor={backgroundColor || token.gray25a}
  />
);

const StateIcon: React.FC<Props> = ({
  isShared,
  entityVisibility,
  backgroundColor,
  hasTooltip,
  entityId,
  entityType,
}) => {
  const { translate } = useTranslate();
  const isEntityChanging = useIsEntityChanging(entityId);
  if (isEntityChanging)
    return (
      <div data-testid="loadingIcon">
        <Spinner />
      </div>
    );

  switch (entityVisibility) {
    case EntityVisibility.SPECIFIC:
      if (hasTooltip) {
        return (
          <Tooltip
            position="top-center"
            content={
              isShared
                ? translate(
                    'ACCESS_MODAL_DROPDOWN.SPECIFIC_STATE_DESCRIPTION_TEXT',
                    { document: entityType.toLowerCase() },
                  )
                : translate(
                    'ACCESS_MODAL_DROPDOWN.PRIVATE_STATE_DESCRIPTION_TEXT',
                  )
            }
          >
            {isShared ? (
              <SpecificUsersIcon backgroundColor={backgroundColor} />
            ) : (
              <LockIcon backgroundColor={backgroundColor} />
            )}
          </Tooltip>
        );
      }

      return isShared ? (
        <SpecificUsersIcon backgroundColor={backgroundColor} />
      ) : (
        <LockIcon backgroundColor={backgroundColor} />
      );

    case EntityVisibility.PORTAL:
      return hasTooltip ? (
        <Tooltip
          position="top-center"
          content={translate(
            entityType === PermissionType.Homepages
              ? 'ACCESS_MODAL_DROPDOWN.PORTAL_STATE_DESCRIPTION_TEXT_HOMEPAGE'
              : 'ACCESS_MODAL_DROPDOWN.PORTAL_STATE_DESCRIPTION_TEXT',
          )}
        >
          <AllPortalUsersIcon backgroundColor={backgroundColor} />
        </Tooltip>
      ) : (
        <AllPortalUsersIcon backgroundColor={backgroundColor} />
      );
    case EntityVisibility.PUBLIC:
      return hasTooltip ? (
        <Tooltip
          position="top-center"
          content={translate(
            'ACCESS_MODAL_DROPDOWN.PUBLIC_STATE_DESCRIPTION_TEXT',
          )}
        >
          <PublicIcon backgroundColor={backgroundColor} />
        </Tooltip>
      ) : (
        <PublicIcon backgroundColor={backgroundColor} />
      );
    default:
      return null;
  }
};

export default StateIcon;
